import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';

export interface CachedObject {
    minValue: number;
    maxValue: number;
    isEnabled: boolean;
    isVisible: boolean;
    sensorId?: number | undefined;
}

@Injectable({
    providedIn: 'root'
})
export class CommandOptimizationRenderingService {
    static $name = 'commandOptimizationRenderingService';

    private cachedObjects: CachedObject[] = [];
    private observables: Array<Subject<CachedObject>> = [];

    getSensorFromCache(sensorId: number) {
        return cloneDeep(this.cachedObjects[sensorId]);
    }

    storeSensorToCache(sensorId: number, obj: CachedObject) {
        if (!sensorId) {
            return;
        }
        if (!this.cachedObjects[sensorId]) {
            this.cachedObjects[sensorId] = {...obj};
            this.observables[sensorId] = new Subject<any>();
        } else {
            this.actualizeCachedObject(sensorId, obj);
        }
    }

    observeSensorChanges(sensorId: number) {
        if (!sensorId) {
            return;
        }
        if (!this.observables[sensorId]) {
            throw new Error('Object with this sensorId not found in cache! ' + sensorId);
        }
        return this.observables[sensorId].asObservable();
    }

    removeSensorFromCache(sensorId: number) {
        if (sensorId) {
            delete this.cachedObjects[sensorId];
            delete this.observables[sensorId];
        }
    }

    refreshCachedSensorAndBroadcastEventIfChanged(sensorId: number, newObj: CachedObject) {
        const originalObj = this.cachedObjects[sensorId];
        if (originalObj) {
            if (
                originalObj.minValue !== newObj.minValue ||
                originalObj.maxValue !== newObj.maxValue ||
                originalObj.isEnabled !== newObj.isEnabled ||
                originalObj.isVisible !== newObj.isVisible
            ) {
                const updates: CachedObject = {
                    minValue: newObj.minValue,
                    maxValue: newObj.maxValue,
                    isEnabled: newObj.isEnabled,
                    isVisible: newObj.isVisible,
                    sensorId,
                };
                this.observables[sensorId].next({...updates});
                this.cachedObjects[sensorId] = {...this.cachedObjects[sensorId], ...updates};
            }
        }
    }

    private actualizeCachedObject(sensorId: number, obj: CachedObject) {
        this.cachedObjects[sensorId] = {...obj};
    }
}
